/**
 * Do not persist JWTs in localStorage (audit: Sensitive Token Storage).
 *
 * Keycloak-angular already holds access/id/refresh tokens on the in-memory
 * Keycloak instance. The SPA reads them via KeycloakService.getToken().
 * Page refresh uses onLoad: 'check-sso' (IdP session cookie), not a stored JWT.
 *
 * OIDC login still needs a short-lived kc-callback-* entry for PKCE/state.
 * That is kept in sessionStorage only, then removed after init.
 */

function isJwt(value: string): boolean {
  return /^eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/.test(value.trim());
}

function looksLikeAuthPayload(value: string): boolean {
  if (!value || typeof value !== "string") {
    return false;
  }
  if (isJwt(value)) {
    return true;
  }
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return parsed.some((item) => typeof item === "string" && isJwt(item));
    }
    if (parsed && typeof parsed === "object") {
      return Object.values(parsed).some(
        (item) => typeof item === "string" && isJwt(item)
      );
    }
  } catch {
    /* not JSON */
  }
  return false;
}

function isAuthStorageKey(key: string): boolean {
  if (!key) {
    return false;
  }
  return (
    key === "token" ||
    key.startsWith("kc-") ||
    key.startsWith("oidc.") ||
    key.includes("access_token") ||
    key.includes("refresh_token") ||
    key.includes("id_token")
  );
}

let installed = false;
let originalSetItem: typeof localStorage.setItem;
let originalGetItem: typeof localStorage.getItem;
let originalRemoveItem: typeof localStorage.removeItem;
let originalKey: typeof localStorage.key;

export function installInMemoryAuthTokenStorage(): void {
  if (installed || typeof window === "undefined" || !window.localStorage) {
    return;
  }

  try {
    originalSetItem = localStorage.setItem.bind(localStorage);
    originalGetItem = localStorage.getItem.bind(localStorage);
    originalRemoveItem = localStorage.removeItem.bind(localStorage);
    originalKey = localStorage.key.bind(localStorage);

    stripPersistedAuthTokens();

    localStorage.setItem = (key: string, value: string) => {
      if (isAuthStorageKey(key) || looksLikeAuthPayload(value)) {
        if (key.startsWith("kc-")) {
          sessionStorage.setItem(key, value);
        }
        originalRemoveItem(key);
        return;
      }
      originalSetItem(key, value);
    };

    localStorage.getItem = (key: string) => {
      if (isAuthStorageKey(key)) {
        if (key.startsWith("kc-")) {
          return sessionStorage.getItem(key);
        }
        return null;
      }
      return originalGetItem(key);
    };

    localStorage.removeItem = (key: string) => {
      if (isAuthStorageKey(key)) {
        sessionStorage.removeItem(key);
      }
      originalRemoveItem(key);
    };

    installed = true;
  } catch (error) {
    console.warn("Could not block auth token persistence in localStorage", error);
  }
}

export function stripPersistedAuthTokens(): void {
  const removeLocal = originalRemoveItem ?? localStorage.removeItem.bind(localStorage);
  const readKey = originalKey ?? localStorage.key.bind(localStorage);

  const localKeys: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = readKey(i);
    if (!key) {
      continue;
    }
    const value = (originalGetItem ?? localStorage.getItem.bind(localStorage))(key);
    if (isAuthStorageKey(key) || (value != null && looksLikeAuthPayload(value))) {
      localKeys.push(key);
    }
  }
  localKeys.forEach((key) => removeLocal(key));

  const sessionKeys: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && (isAuthStorageKey(key) && !key.startsWith("kc-"))) {
      sessionKeys.push(key);
    }
  }
  sessionKeys.forEach((key) => sessionStorage.removeItem(key));
}

export function clearAuthHandshakeStorage(): void {
  stripPersistedAuthTokens();
  const kcKeys: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key?.startsWith("kc-")) {
      kcKeys.push(key);
    }
  }
  kcKeys.forEach((key) => sessionStorage.removeItem(key));
}
